import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
  Box,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { FC, ReactNode, useState } from 'react';
import strings from 'common/Translation/Translate';
import { FormValidators } from '../Form';
import { validateInput } from '../../utils/validators/Validators';

type Option = { label: string; value: string; iconKey?: 'company' | 'individual' };

type Props = {
  field?: string;
  disabled?: boolean;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  options?: Option[];
  inputValue?: string | false;
  onChange: (value: string | boolean, field: string, error?: { error: boolean; message: string }) => void;
};

const toggleGroupSx = {
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.5,
    fontSize: '0.8125rem',
    textTransform: 'none',
    fontWeight: 500,
    gap: 0.5,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    fontWeight: 700,
    color: 'primary.contrastText',
    bgcolor: 'primary.main',
    '&:hover': {
      bgcolor: 'primary.dark',
    },
  },
};

const optionIcon = (iconKey?: Option['iconKey']): ReactNode => {
  if (iconKey === 'company') return <CorporateFareOutlinedIcon sx={{ fontSize: 16 }} />;
  if (iconKey === 'individual') return <PersonOutlineOutlinedIcon sx={{ fontSize: 16 }} />;
  return null;
};

const ToggleButtonGroupInput: FC<Props> = ({
  field,
  disabled,
  validators,
  hasError,
  fieldError,
  options = [],
  inputValue,
  onChange,
}) => {
  const [error, setError] = useState<{ error: string; message: string }>();
  const selectedValue = inputValue || inputValue === '0' ? String(inputValue) : '';

  const handleChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value == null || !field) return;
    const inputError = validateInput(validators, value);
    setError(inputError);
    onChange(value, field, inputError || undefined);
  };

  const helperMessage = error?.error ? error.message : !error && hasError && fieldError ? strings.requiredText : '';

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={selectedValue}
        onChange={handleChange}
        disabled={disabled}
        sx={toggleGroupSx}>
        {options.map(option => (
          <ToggleButton key={option.value} value={option.value}>
            {optionIcon(option.iconKey)}
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {helperMessage ? <FormHelperText error>{helperMessage}</FormHelperText> : null}
    </Box>
  );
};

export default ToggleButtonGroupInput;
